"use client";

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { cn } from "@/utils/utils";
import { getEditorHasUnsavedChanges } from "@/utils/editor-unsaved-guard";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { SidebarNavIcon } from "@/components/sidebar/sidebar-nav-icons";
import { useSidebar } from "@/components/ui/sidebar";
import { NavMainItem, NavSection } from "@/components/sidebar/nav-types";
import { useSidebarPreferences } from "@/contexts/sidebar-preferences-context";

const BADGE_CLASSNAME =
  "shrink-0 rounded-full bg-blue-100 px-1.5 py-0 text-[10px] font-normal leading-normal text-blue-700";

interface NavMainProps extends NavSection {
  allowedMap: Map<string, boolean>;
}

export const NavMain = ({
  title,
  items,
  allowedMap,
  isCollapsible: isCollapsibleProp,
  defaultOpen: defaultOpenProp,
  badge,
}: NavMainProps) => {
  const pathname = usePathname();
  const { isMobile, setOpenMobile } = useSidebar();
  const isActive = (url: string) =>
    pathname === url || pathname.startsWith(`${url}/`);

  const isCollapsible = Boolean(title) && (isCollapsibleProp ?? true);

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (!item.permissions) {
          return true; // No permissions required, allow by default
        }
        return allowedMap.get(JSON.stringify(item.permissions)) ?? true;
      }),
    [items, allowedMap],
  );

  const hasActiveItem = useMemo(
    () => filteredItems.some((item) => isActive(item.url)),
    [filteredItems, pathname],
  );

  const defaultOpen = (defaultOpenProp ?? true) || hasActiveItem;
  const { preferences, setCategoryOpen } = useSidebarPreferences();
  const isCategoryOpen = preferences.categories[title ?? ""] ?? defaultOpen;

  if (filteredItems.length === 0) {
    return null;
  }

  const closeMobileSidebar = () => {
    // Keep the drawer open while unsaved-changes confirmation is shown.
    if (isMobile && !getEditorHasUnsavedChanges()) {
      setOpenMobile(false);
    }
  };

  const renderMenuItem = (item: NavMainItem) => {
    const linkContent = (
      <Link href={item.url} onClick={closeMobileSidebar}>
        {item.icon && (
          <SidebarNavIcon icon={item.icon} className="size-4 shrink-0" />
        )}
        <span
          data-sidebar="menu-label"
          className={cn(item.isDisabled && "opacity-60")}
        >
          {item.title}
        </span>
      </Link>
    );

    return (
      <SidebarMenuItem
        key={item.title}
        className={cn(item.isDisabled && "pointer-events-none opacity-60")}
      >
        <SidebarMenuButton
          asChild
          isActive={isActive(item.url)}
          tooltip={item.title}
        >
          {linkContent}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const renderNestedItem = (item: NavMainItem) => (
    <Collapsible
      key={item.title}
      asChild
      defaultOpen={item.defaultOpen || isActive(item.url)}
      className="group/collapsible"
    >
      <SidebarMenuItem
        className={cn(item.isDisabled && "pointer-events-none opacity-60")}
      >
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            {item.icon && (
              <SidebarNavIcon icon={item.icon} className="size-4 shrink-0" />
            )}
            <span data-sidebar="menu-label">{item.title}</span>
            <ChevronRight
              size={14}
              data-sidebar="menu-chevron"
              className="ml-auto size-[14px] shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-0">
          <SidebarMenu>
            {item.items?.map((subItem) => renderMenuItem(subItem))}
          </SidebarMenu>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );

  const renderItems = () =>
    filteredItems.map((item) =>
      item.items?.length ? renderNestedItem(item) : renderMenuItem(item),
    );

  const menuContent = <SidebarMenu>{renderItems()}</SidebarMenu>;

  const groupLabel = title && (
    <SidebarGroupLabel
      className={cn(
        "flex items-center gap-2",
        isCollapsible &&
          "cursor-pointer rounded-[6px] transition-colors data-[state=closed]:hover:bg-black/[0.03] data-[state=open]:hover:bg-transparent data-[state=open]:hover:text-[#6d6e71]",
        !isCollapsible && "pointer-events-none",
      )}
    >
      <span>{title}</span>
      {badge && (
        <Badge variant="secondary" className={BADGE_CLASSNAME}>
          {badge}
        </Badge>
      )}
      {isCollapsible && (
        <ChevronRight
          size={14}
          className="ml-auto size-[14px] shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
        />
      )}
    </SidebarGroupLabel>
  );

  const content = isCollapsible ? (
    <Collapsible
      open={isCategoryOpen}
      onOpenChange={(open) => {
        if (title) {
          setCategoryOpen(title, open);
        }
      }}
      className="group/collapsible flex flex-col gap-[2px]"
    >
      <CollapsibleTrigger asChild>{groupLabel}</CollapsibleTrigger>
      <CollapsibleContent className="p-0">{menuContent}</CollapsibleContent>
    </Collapsible>
  ) : (
    <div className="flex flex-col gap-[2px]">
      {groupLabel}
      {menuContent}
    </div>
  );

  return <SidebarGroup>{title ? content : menuContent}</SidebarGroup>;
};
